import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './AnnouncementManagement.module.css';
import { FiEdit, FiTrash2, FiPlus, FiUpload, FiFileText } from 'react-icons/fi';

const AnnouncementManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    source: '',
    name: '',
    description: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch('/api/announcement');
      if (response.ok) {
        const data = await response.json();
        setNews(data);
      } else {
        alert('Ошибка при загрузке новостей');
      }
    } catch (error) {
      alert('Ошибка при загрузке новостей');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      // Создаем превью для отображения
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingNews ? `/api/admin/announcement/${editingNews.id}` : '/api/admin/announcement';
      const method = editingNews ? 'PUT' : 'POST';

      // Всегда отправляем FormData
      const formDataToSend = new FormData();
      formDataToSend.append('source', formData.source);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);

      // Добавляем изображение только если оно выбрано
      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      const response = await fetch(url, {
        method,
        body: formDataToSend,
      });

      if (response.ok) {
        const result = await response.json();
        if (editingNews) {
          setNews(prev => prev.map(item =>
              item.id === editingNews.id ? result : item
          ));
          alert('Новость обновлена');
        } else {
          setNews(prev => [...prev, result]);
          alert('Новость добавлена');
        }
        handleCancel();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Ошибка при сохранении новости');
      }
    } catch (error) {
      alert('Ошибка при сохранении новости');
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      source: newsItem.source,
      name: newsItem.name,
      description: newsItem.description
    });
    // Устанавливаем URL изображения для превью при редактировании только если есть реальный URL
    const hasImage = newsItem.img && newsItem.img.trim() !== '' && newsItem.img !== 'true';
    setImagePreview(hasImage ? newsItem.img : '');
    setSelectedImage(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту новость?')) {
      try {
        const response = await fetch(`/api/admin/news/${id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          setNews(prev => prev.filter(item => item.id !== id));
          alert('Новость удалена');
        } else {
          alert('Ошибка при удалении новости');
        }
      } catch (error) {
        alert('Ошибка при удалении новости');
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingNews(null);
    setFormData({
      source: '',
      name: '',
      description: ''
    });
    setSelectedImage(null);
    setImagePreview('');
  };

  const handleAddNew = () => {
    setEditingNews(null);
    setFormData({
      source: '',
      name: '',
      description: ''
    });
    setSelectedImage(null);
    setImagePreview('');
    setShowForm(true);
  };

  if (loading) {
    return <div className={s.loading}>Загрузка...</div>;
  }

  return (
      <div className={s.container}>
        <div className={s.header}>
          <h1>Редактор анонсов</h1>
          <div className={s.headerActions}>
            <button
                className={s.addButton}
                onClick={handleAddNew}
                disabled={news.length >= 9}
            >
              <FiPlus /> Добавить новость
            </button>
            <button
                className={s.editorButton}
                onClick={() => navigate('/admin/articles')}
            >
              <FiFileText /> Редактор статей
            </button>
          </div>
        </div>

        {news.length >= 9 && (
            <div className={s.limitWarning}>
              Достигнут лимит новостей (максимум 9)
            </div>
        )}

        {showForm && (
            <div className={s.formOverlay}>
              <div className={s.formContainer}>
                <h2>{editingNews ? 'Редактировать новость' : 'Добавить новость'}</h2>
                <form onSubmit={handleSubmit} className={s.form}>
                  <div className={s.formGroup}>
                    <label>Источник:</label>
                    <input
                        type="text"
                        name="source"
                        value={formData.source}
                        onChange={handleInputChange}
                        required
                        placeholder="Ссылка на источник"
                    />
                  </div>

                  <div className={s.formGroup}>
                    <label>Название:</label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Название новости"
                    />
                  </div>

                  <div className={s.formGroup}>
                    <label>Изображение:</label>
                    <div className={s.imageUploadContainer}>
                      <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className={s.fileInput}
                          id="imageInput"
                      />
                      <label htmlFor="imageInput" className={s.fileInputLabel}>
                        <FiUpload /> {selectedImage ? selectedImage.name : 'Выберите изображение'}
                      </label>
                    </div>
                    {imagePreview && (
                        <div className={s.imagePreview}>
                          <img
                              src={imagePreview}
                              alt="Превью"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                          />
                          <div
                              style={{
                                display: 'none',
                                width: '100%',
                                height: '150px',
                                backgroundColor: '#f0f0f0',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#666',
                                fontSize: '14px'
                              }}
                          >
                            Изображение недоступно
                          </div>
                        </div>
                    )}
                  </div>

                  <div className={s.formGroup}>
                    <label>Описание:</label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        placeholder="Описание новости"
                        rows="4"
                    />
                  </div>

                  <div className={s.formActions}>
                    <button type="submit" className={s.saveButton}>
                      {editingNews ? 'Обновить' : 'Добавить'}
                    </button>
                    <button type="button" onClick={handleCancel} className={s.cancelButton}>
                      Отмена
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        <div className={s.newsGrid}>
          {news.map((newsItem) => {
            const hasImage = newsItem.img && newsItem.img.trim() !== '' && newsItem.img !== 'true';

            return (
                <div key={newsItem.id} className={s.newsCard}>
                  <div className={s.newsImage}>
                    {hasImage ? (
                        <img
                            src={newsItem.img}
                            alt={newsItem.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                        />
                    ) : null}
                    <div
                        style={{
                          display: hasImage ? 'none' : 'block',
                          width: '100%',
                          height: '200px',
                          backgroundColor: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#666'
                        }}
                    >
                      {hasImage ? 'Изображение недоступно' : 'Нет изображения'}
                    </div>
                  </div>
                  <div className={s.newsContent}>
                    <h3>{newsItem.name}</h3>
                    <p>{newsItem.description}</p>
                    <div className={s.newsMeta}>
                      <span>Источник: {newsItem.source}</span>
                      <span>Создано: {new Date(newsItem.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className={s.newsActions}>
                    <button
                        onClick={() => handleEdit(newsItem)}
                        className={s.editButton}
                        title="Редактировать"
                    >
                      <FiEdit />
                    </button>
                    <button
                        onClick={() => handleDelete(newsItem.id)}
                        className={s.deleteButton}
                        title="Удалить"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
            );
          })}
        </div>

                {news.length === 0 && !loading && (
            <div className={s.emptyState}>
              <p>Новости не найдены</p>
            </div>
        )}


      </div>
    );
  };

export default AnnouncementManagement;